/**
 * Armada Filter Panel
 * Handles: filter panel open/close (desktop push / mobile overlay),
 * sort custom dropdown wired to facets.js, mobile sort radios in the panel,
 * Lil' Enzo penguin toggle, small/large tile-size toggle.
 */

(function () {
  'use strict';

  const PANEL_ID = 'ArmadaFilterPanel';
  const OPEN_CLASS = 'is-open';
  const BODY_LOCK_CLASS = 'armada-panel-open';
  const DESKTOP_BREAKPOINT = 990;

  // -------------------------------------------------------------------------
  // Filter Panel
  // -------------------------------------------------------------------------
  function getPanel() {
    return document.getElementById(PANEL_ID);
  }

  function isDesktop() {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  function openPanel() {
    const panel = getPanel();
    if (!panel) return;
    panel.classList.add(OPEN_CLASS);
    panel.setAttribute('aria-hidden', 'false');

    if (!isDesktop()) {
      document.body.classList.add(BODY_LOCK_CLASS);
      // AAS-5634: below 990px sorting lives in this layer ("Filters | Sort"), so
      // opening it is the sort intent the bar's dropdown signals on desktop.
      if (panel.querySelector('.armada-filter-sort')) prefetchSortOptions();
    }

    // Update aria-expanded on the toggle button
    const toggleBtn = document.querySelector('[data-armada-filter-toggle]');
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');

    // Move focus to close button for accessibility. AAS-5625: preventScroll —
    // opening the filter must never move the page, the shopper keeps their
    // place in the grid.
    const closeBtn = panel.querySelector('.armada-filter-panel__close');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus({ preventScroll: true }), 50);
    }
  }

  function closePanel() {
    const panel = getPanel();
    if (!panel) return;
    panel.classList.remove(OPEN_CLASS);
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_LOCK_CLASS);

    // Update aria-expanded on the toggle button
    const toggleBtn = document.querySelector('[data-armada-filter-toggle]');
    if (toggleBtn) {
      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.focus({ preventScroll: true });
    }
  }

  function togglePanel() {
    const panel = getPanel();
    if (!panel) return;
    if (panel.classList.contains(OPEN_CLASS)) {
      closePanel();
    } else {
      openPanel();
    }
  }

  // -------------------------------------------------------------------------
  // Sort Dropdown
  // -------------------------------------------------------------------------
  // AAS-5792: a sort result is a 1–2 s Shopify section render. Start fetching
  // every sort option for the current filters as soon as the shopper shows
  // intent (hover/focus/open), so the pick renders from the facets.js cache.
  function prefetchSortOptions() {
    customElements.get('facet-filters-form')?.prefetchSortOptions?.();
  }

  function openSortDropdown(btn) {
    const dropdown = btn.closest('.collection-filter-bar__sort');
    if (!dropdown) return;
    prefetchSortOptions();
    dropdown.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');

    const list = dropdown.querySelector('.collection-filter-bar__sort-list');
    if (list) list.setAttribute('aria-hidden', 'false');
  }

  function closeSortDropdown(btn) {
    const dropdown = btn ? btn.closest('.collection-filter-bar__sort') : document.querySelector('.collection-filter-bar__sort.is-open');
    if (!dropdown) return;
    dropdown.classList.remove('is-open');
    const toggleBtn = dropdown.querySelector('[data-armada-sort-toggle]');
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    const list = dropdown.querySelector('.collection-filter-bar__sort-list');
    if (list) list.setAttribute('aria-hidden', 'true');
  }

  function closeAllSortDropdowns() {
    document.querySelectorAll('.collection-filter-bar__sort.is-open').forEach((dropdown) => {
      dropdown.classList.remove('is-open');
      const btn = dropdown.querySelector('[data-armada-sort-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      const list = dropdown.querySelector('.collection-filter-bar__sort-list');
      if (list) list.setAttribute('aria-hidden', 'true');
    });
  }

  function selectSortOption(option) {
    const value = option.dataset.sortValue;
    if (!value) return;

    // Update the hidden select in FacetSortDrawerForm and trigger facets.js
    // facet-filters-form listens for `input` events on its form
    const hiddenSelect = document.getElementById('ArmadaSortBy');
    if (hiddenSelect) {
      hiddenSelect.value = value;
      hiddenSelect.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Update the displayed label in the button
    const dropdown = option.closest('.collection-filter-bar__sort');
    if (dropdown) {
      const label = dropdown.querySelector('.collection-filter-bar__sort-label');
      if (label) label.textContent = option.textContent.trim();
      syncSortButtonAria(dropdown.querySelector('.collection-filter-bar__sort-btn'), option.textContent.trim());

      // Mark the selected option
      dropdown.querySelectorAll('.collection-filter-bar__sort-option').forEach((opt) => {
        opt.classList.toggle('is-selected', opt === option);
        opt.setAttribute('aria-selected', opt === option ? 'true' : 'false');
      });
    }

    closeSortDropdown(null);
  }

  // AAS-5634: mobile sorting lives in the filter layer as radios. A pick takes
  // the same path as the desktop dropdown — the hidden #ArmadaSortBy select
  // fires `input` and facets.js submits it at once.
  function selectSortRadio(radio) {
    const hiddenSelect = document.getElementById('ArmadaSortBy');
    if (!hiddenSelect || !radio.value || hiddenSelect.value === radio.value) return;
    hiddenSelect.value = radio.value;
    hiddenSelect.dispatchEvent(new Event('input', { bubbles: true }));
    reinitSortLabel();
  }

  // Arrow keys move through a radio group by CHECKING each option, so every
  // step fires `change`. A tap or click sorts at once; keyboard steps settle
  // first, so walking the list sorts once — by the option the shopper stops on.
  const SORT_KEYBOARD_DELAY = 600;
  let sortRadioTimer = null;
  let sortPointerAt = 0;

  function handleSortRadioChange(radio) {
    clearTimeout(sortRadioTimer);
    const fromPointer = Date.now() - sortPointerAt < SORT_KEYBOARD_DELAY;
    if (fromPointer) {
      selectSortRadio(radio);
      return;
    }
    sortRadioTimer = setTimeout(() => {
      if (radio.checked) selectSortRadio(radio);
    }, SORT_KEYBOARD_DELAY);
  }

  // -------------------------------------------------------------------------
  // Tile size — AAS-5634
  // Small (default) or large product tiles. The state is a class on
  // .armada-plp-layout, which sits outside #ProductGridContainer, so facets.js
  // re-renders and load-more appends inherit it without re-applying. The column
  // counts per mode live in armada-collection-product-grid.css. The section's
  // inline script applies the saved size before first paint.
  // -------------------------------------------------------------------------
  const TILE_SIZE_KEY = 'armada:plp-tile-size';
  const TILE_LARGE_CLASS = 'armada-grid--large';

  function getTileSize() {
    const layout = document.querySelector('.armada-plp-layout');
    return layout && layout.classList.contains(TILE_LARGE_CLASS) ? 'large' : 'small';
  }

  // Tiles render the small-tile `sizes`; card-product-v2 stores both values on
  // the tile so a large tile can ask for a large enough srcset candidate.
  // Only media whose `sizes` matches the tile's own value is touched, never
  // swatch thumbnails.
  function syncTileImageSizes(tiles = document.querySelectorAll('[data-img-sizes-large]')) {
    const large = getTileSize() === 'large';
    tiles.forEach((tile) => {
      const from = large ? tile.dataset.imgSizes : tile.dataset.imgSizesLarge;
      const to = large ? tile.dataset.imgSizesLarge : tile.dataset.imgSizes;
      if (!from || !to) return;
      tile.querySelectorAll('img[sizes], source[sizes]').forEach((el) => {
        if (el.getAttribute('sizes') === from) el.setAttribute('sizes', to);
      });
    });
  }

  // The switch is a short fade on the grid list itself: out, swap the layout,
  // back in. Only #product-grid is animated (Web Animations, handles kept here)
  // — never the tiles, whose own scroll-reveal animations hold them visible and
  // must not be touched. Reduced motion: the switch is instant.
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TILE_FADE_OUT = 120;
  const TILE_FADE_IN = 220;
  // The size the grid is switching to; null once the layout matches it.
  let pendingTileSize = null;
  let tileFadeOut = null;
  let tileFadeIn = null;

  // Keep the shopper's place across the reflow: the product under the top edge
  // of the visible grid (below the sticky filter bar) keeps its offset from that
  // edge. Promo tiles are skipped — their rows are re-resolved per mode.
  function gridViewportTop() {
    const bar = document.getElementById('CollectionFilterBar');
    const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
    return Math.max(0, Math.min(barBottom, window.innerHeight));
  }

  function findTileAnchor(grid) {
    const edge = gridViewportTop();
    let covering = null;
    let below = null;
    grid.querySelectorAll(':scope > .armada-product-grid__item:not([hidden])').forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (!rect.height || rect.bottom <= edge || rect.top >= window.innerHeight) return;
      if (rect.top <= edge) {
        if (!covering || rect.left < covering.rect.left) covering = { item, rect };
      } else if (!below || rect.top < below.rect.top || (rect.top === below.rect.top && rect.left < below.rect.left)) {
        below = { item, rect };
      }
    });
    const anchor = covering || below;
    return anchor ? { item: anchor.item, offset: anchor.rect.top - edge } : null;
  }

  function commitTileSize() {
    const size = pendingTileSize;
    pendingTileSize = null;
    const layout = document.querySelector('.armada-plp-layout');
    if (!layout || !size || size === getTileSize()) return;

    const grid = document.getElementById('product-grid');
    const anchor = grid ? findTileAnchor(grid) : null;

    layout.classList.toggle(TILE_LARGE_CLASS, size === 'large');
    syncTileImageSizes();
    // product-tile-v2.js re-pins the desktop card heights to the new widths.
    document.dispatchEvent(new CustomEvent('armada:tile-size-change', { detail: { size } }));
    // Promo rows are resolved for both modes' column counts, but re-run so a
    // tile waiting on a row picks up anything load more added meanwhile.
    if (typeof window.armadaPlacePromoTiles === 'function') {
      document.querySelectorAll('.armada-product-grid').forEach(window.armadaPlacePromoTiles);
    }

    if (anchor && anchor.item.isConnected) {
      const shift = anchor.item.getBoundingClientRect().top - gridViewportTop() - anchor.offset;
      if (Math.abs(shift) > 1) window.scrollBy(0, shift);
    }
  }

  function runTileSizeSwitch() {
    const grid = document.getElementById('product-grid');
    if (!grid || reducedMotion.matches || typeof grid.animate !== 'function') {
      commitTileSize();
      return;
    }
    // Already fading out: that fade commits whatever size is pending by then.
    if (tileFadeOut) return;

    // Mid fade-in: fade out from where the opacity is now, no snap to 1.
    const from = getComputedStyle(grid).opacity;
    tileFadeIn?.cancel();
    tileFadeIn = null;

    const fadeOut = grid.animate([{ opacity: from }, { opacity: 0 }], {
      duration: TILE_FADE_OUT,
      easing: 'ease-in',
      fill: 'forwards',
    });
    tileFadeOut = fadeOut;
    fadeOut.finished
      .then(() => {
        commitTileSize();
        const fadeIn = grid.animate([{ opacity: 0 }, { opacity: 1 }], { duration: TILE_FADE_IN, easing: 'ease-out' });
        tileFadeIn = fadeIn;
        fadeIn.finished.then(() => {
          if (tileFadeIn === fadeIn) tileFadeIn = null;
        }, () => {});
      })
      .catch(() => {
        // Grid replaced mid-fade (facets.js re-render) — still land the size.
        commitTileSize();
      })
      .finally(() => {
        fadeOut.cancel();
        if (tileFadeOut === fadeOut) tileFadeOut = null;
      });
  }

  function setTileSize(size) {
    if (!document.querySelector('.armada-plp-layout')) return;
    if (size === (pendingTileSize || getTileSize())) return;
    pendingTileSize = size;

    document.querySelectorAll('[data-armada-tile-size]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.armadaTileSize === size ? 'true' : 'false');
    });
    try {
      localStorage.setItem(TILE_SIZE_KEY, size === 'large' ? 'large' : 'small');
    } catch (e) {
      // Storage blocked (private mode) — the toggle still works for this page.
    }

    runTileSizeSwitch();
  }

  // -------------------------------------------------------------------------
  // Lil' Enzo — Penguin lifestyle image toggle
  // -------------------------------------------------------------------------
  function toggleLifestyleMode(btn) {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    const isActive = btn.classList.toggle('is-active');
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    // Reverse each card's image order: the tile then leads with its hover
    // image (PIM overview[1]) and reveals the main image (overview[0]) on
    // hover — the inverse of the default card. Handled entirely in CSS via
    // this class on the grid (see product-tile-v2.css → .armada-lifestyle-mode);
    // the class is also re-applied by the MutationObserver below after facets.js
    // swaps the grid on pagination/filtering.
    grid.classList.toggle('armada-lifestyle-mode', isActive);
  }

  // -------------------------------------------------------------------------
  // Keyboard trap for mobile overlay
  // -------------------------------------------------------------------------
  function handlePanelKeydown(e) {
    const panel = getPanel();
    if (!panel || !panel.classList.contains(OPEN_CLASS)) return;
    if (isDesktop()) return; // only trap on mobile

    if (e.key === 'Escape') {
      closePanel();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  // -------------------------------------------------------------------------
  // Promo tile visibility — hide only when a filter is selected
  // -------------------------------------------------------------------------
  function hasActiveFilters() {
    // Check the hidden active-facets-mobile list that facets.js keeps up to date
    const mobileActive = document.querySelector('.active-facets-mobile');
    if (mobileActive && mobileActive.querySelectorAll('li').length > 0) return true;
    // Also check URL params (handles initial page load with pre-set filters)
    return /[?&]filter\./.test(window.location.search);
  }

  function updatePromoTileVisibility() {
    const layout = document.querySelector('.armada-plp-layout');
    if (layout) layout.classList.toggle('armada-has-active-filters', hasActiveFilters());
  }

  // -------------------------------------------------------------------------
  // Event delegation
  // -------------------------------------------------------------------------
  function handleClick(e) {
    // Filter panel toggle button
    if (e.target.closest('[data-armada-filter-toggle]')) {
      e.preventDefault();
      togglePanel();
      return;
    }

    // Close panel via close button
    if (e.target.closest('[data-armada-panel-close]')) {
      closePanel();
      return;
    }

    // Sort toggle button
    const sortBtn = e.target.closest('[data-armada-sort-toggle]');
    if (sortBtn) {
      e.preventDefault();
      const dropdown = sortBtn.closest('.collection-filter-bar__sort');
      if (dropdown && dropdown.classList.contains('is-open')) {
        closeSortDropdown(sortBtn);
      } else {
        closeAllSortDropdowns();
        openSortDropdown(sortBtn);
      }
      return;
    }

    // Sort option selection
    const sortOption = e.target.closest('.collection-filter-bar__sort-option');
    if (sortOption) {
      e.preventDefault();
      selectSortOption(sortOption);
      return;
    }

    // Tile size toggle
    const tileSizeBtn = e.target.closest('[data-armada-tile-size]');
    if (tileSizeBtn) {
      e.preventDefault();
      setTileSize(tileSizeBtn.dataset.armadaTileSize);
      return;
    }

    // Penguin / Lil' Enzo toggle
    const penguinBtn = e.target.closest('[data-armada-penguin-toggle]');
    if (penguinBtn) {
      e.preventDefault();
      toggleLifestyleMode(penguinBtn);
      return;
    }

    // Click outside sort dropdown — close it
    if (!e.target.closest('.collection-filter-bar__sort')) {
      closeAllSortDropdowns();
    }
  }

  // AAS-5792 (a11y): the options are focusable but had no key handling, so the
  // sort could not be changed from the keyboard. Enter/Space pick, arrows move,
  // Escape closes and returns focus to the sort button.
  function handleSortOptionKeydown(e) {
    const toggleBtn = e.target.closest?.('[data-armada-sort-toggle]');
    if (toggleBtn && e.key === 'ArrowDown') {
      e.preventDefault();
      openSortDropdown(toggleBtn);
      const dropdown = toggleBtn.closest('.collection-filter-bar__sort');
      dropdown?.querySelector('.collection-filter-bar__sort-option.is-selected, .collection-filter-bar__sort-option')?.focus();
      return true;
    }

    const option = e.target.closest?.('.collection-filter-bar__sort-option');
    if (!option) return false;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const toggleBtn = option.closest('.collection-filter-bar__sort')?.querySelector('[data-armada-sort-toggle]');
      selectSortOption(option);
      toggleBtn?.focus({ preventScroll: true });
      return true;
    }

    if (e.key === 'Escape') {
      const toggleBtn = option.closest('.collection-filter-bar__sort')?.querySelector('[data-armada-sort-toggle]');
      closeAllSortDropdowns();
      toggleBtn?.focus({ preventScroll: true });
      return true;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const sibling = e.key === 'ArrowDown' ? option.nextElementSibling : option.previousElementSibling;
      sibling?.focus();
      return true;
    }

    return false;
  }

  function handleKeydown(e) {
    if (handleSortOptionKeydown(e)) return;

    // Close panel on Escape
    if (e.key === 'Escape') {
      const panel = getPanel();
      if (panel && panel.classList.contains(OPEN_CLASS)) {
        closePanel();
        return;
      }
      // Also close sort dropdown
      closeAllSortDropdowns();
    }

    handlePanelKeydown(e);
  }

  // -------------------------------------------------------------------------
  // Re-initialise after facets.js re-renders the section
  // -------------------------------------------------------------------------
  // AAS-5226 (a11y): the button's aria-label is "Sort: <option>" so the
  // visible text stays part of the accessible name — re-compose it whenever
  // the visible label is rewritten client-side.
  function syncSortButtonAria(btn, optionText) {
    if (!btn || !optionText) return;
    const prefix = btn.dataset.sortAriaPrefix;
    if (prefix) btn.setAttribute('aria-label', `${prefix}: ${optionText}`);
  }

  function reinitSortLabel() {
    const hiddenSelect = document.getElementById('ArmadaSortBy');
    if (!hiddenSelect) return;
    const selected = hiddenSelect.options[hiddenSelect.selectedIndex];
    if (!selected) return;
    const label = document.querySelector('.collection-filter-bar__sort-label');
    if (label) label.textContent = selected.text;
    syncSortButtonAria(document.querySelector('.collection-filter-bar__sort-btn'), selected.text);

    // Sync the visual option list
    const value = selected.value;
    document.querySelectorAll('.collection-filter-bar__sort-option').forEach((opt) => {
      const isSelected = opt.dataset.sortValue === value;
      opt.classList.toggle('is-selected', isSelected);
      opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    // AAS-5634: and the mobile sort radios in the filter layer
    document.querySelectorAll('[data-armada-sort-radio]').forEach((radio) => {
      radio.checked = radio.value === value;
      radio.closest('.armada-filter-option')?.classList.toggle('active', radio.checked);
    });
  }

  // -------------------------------------------------------------------------
  // Prevent facets.js from auto-scrolling the page after a filter change
  // facets.js calls window.scrollTo({ top: ..., behavior: 'smooth' }) after re-rendering
  // -------------------------------------------------------------------------
  function suppressFacetsScroll() {
    if (!document.getElementById('CollectionFilterBar')) return;
    const nativeScrollTo = window.scrollTo.bind(window);
    window.scrollTo = function (x, y) {
      // If called with a smooth scroll object it's the facets.js post-filter scroll — ignore
      if (x !== null && typeof x === 'object' && x.behavior === 'smooth') return;
      nativeScrollTo.call(window, x, y);
    };
  }

  // -------------------------------------------------------------------------
  // Measure the filter bar height and expose as a CSS custom property
  // so the sticky filter panel can position itself directly below the bar.
  // -------------------------------------------------------------------------
  function syncFilterBarHeight() {
    const bar = document.getElementById('CollectionFilterBar');
    if (!bar) return;
    const height = bar.offsetHeight;
    document.documentElement.style.setProperty('--filter-bar-height', height + 'px');
  }

  // -------------------------------------------------------------------------
  // PLP header auto-hide on scroll
  // On collection pages: scrolling down past the filter bar hides the nav
  // header so only the filter bar sticks at top. Scrolling up reveals it.
  // -------------------------------------------------------------------------
  function initHeaderAutoHide() {
    // Only on collection (PLP) pages
    if (!document.body.classList.contains('template-collection')) return;
    if (!isDesktop()) return;

    var lastScrollY = window.scrollY;
    var headerHidden = false;
    var headerSection = document.querySelector('.section-header.shopify-section-group-header-group');
    var filterBar = document.getElementById('CollectionFilterBar');
    if (!headerSection || !filterBar) return;

    var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 56;

    // Compute the absolute scroll position where the filter bar starts to stick.
    // This is the bar's initial document position minus the header height (since
    // the bar sticks at top: header-height).
    var barAbsoluteTop = filterBar.getBoundingClientRect().top + window.scrollY;
    var stickyThreshold = barAbsoluteTop - headerHeight;

    function hideHeader() {
      if (headerHidden) return;
      headerHidden = true;
      // shopify-section-header-sticky is required so that `top` on the sticky
      // element actually takes effect (matches how StickyHeader.hide() works).
      headerSection.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      document.documentElement.style.setProperty('--header-visible-height', '0px');
    }

    function showHeader() {
      if (!headerHidden) return;
      // If the filter panel is open and we're still past the threshold,
      // keep the header hidden so it doesn't slide in over the filters.
      var panelEl = getPanel();
      if (panelEl && panelEl.classList.contains(OPEN_CLASS) && window.scrollY >= stickyThreshold) return;
      headerHidden = false;
      headerSection.classList.remove('shopify-section-header-hidden');
      headerSection.classList.add('animate');
      document.documentElement.style.removeProperty('--header-visible-height');
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var currentScrollY = window.scrollY;

        if (currentScrollY > lastScrollY && currentScrollY >= stickyThreshold) {
          // Scrolling DOWN past the filter bar → hide header
          hideHeader();
        } else if (currentScrollY < lastScrollY) {
          // Scrolling UP → show header
          showHeader();
        }

        lastScrollY = currentScrollY;
        ticking = false;
      });
    }, { passive: true });

    // Also re-check on resize (desktop ↔ mobile)
    window.addEventListener('resize', function () {
      if (!isDesktop() && headerHidden) {
        showHeader();
      }
    });
  }

  // -------------------------------------------------------------------------
  // Init
  // -------------------------------------------------------------------------
  function handleSortIntent(e) {
    if (e.target.closest?.('[data-armada-sort-toggle]')) prefetchSortOptions();
  }

  function handleChange(e) {
    if (e.target.matches?.('[data-armada-sort-radio]')) handleSortRadioChange(e.target);
  }

  function handleSortRadioPointer(e) {
    if (e.target.closest?.('.armada-filter-sort')) sortPointerAt = Date.now();
  }

  function init() {
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('pointerdown', handleSortRadioPointer, { passive: true });
    document.addEventListener('pointerover', handleSortIntent, { passive: true });
    document.addEventListener('focusin', handleSortIntent);
    document.addEventListener('keydown', handleKeydown);
    suppressFacetsScroll();
    reinitSortLabel();
    syncFilterBar();
    syncFilterBarHeight();
    window.addEventListener('resize', syncFilterBarHeight);
    // Set initial promo tile state based on URL filters (page load with filters in URL)
    updatePromoTileVisibility();
    initHeaderAutoHide();
    syncTileImageSizes();
  }

  // -------------------------------------------------------------------------
  // Sync filter bar count badge + tag pills from .active-facets-mobile
  // Called after facets.js re-renders filter state via section rendering API
  // -------------------------------------------------------------------------
  function syncFilterBar() {
    // Count active filter tags from the panel's active-facets-mobile element
    const activeFacetsMobile = document.querySelector('.active-facets-mobile');
    const count = activeFacetsMobile ? activeFacetsMobile.querySelectorAll('facet-remove').length : 0;

    // Update the filter bar's count badge
    const filterBtn = document.querySelector('[data-armada-filter-toggle]');
    if (filterBtn) {
      let badge = filterBtn.querySelector('.collection-filter-bar__filter-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'collection-filter-bar__filter-count';
          filterBtn.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }

      // Toggle active state on filter button
      const isPanelOpen = document.getElementById(PANEL_ID)?.classList.contains(OPEN_CLASS);
      filterBtn.setAttribute('aria-expanded', isPanelOpen ? 'true' : 'false');
    }

    // Sync the desktop tag pills in the filter bar
    const barTagsContainer = document.querySelector('.collection-filter-bar__tags');
    if (barTagsContainer && activeFacetsMobile) {
      // Build new tag HTML from the panel's active tags
      const tags = activeFacetsMobile.querySelectorAll('facet-remove');
      if (tags.length === 0) {
        barTagsContainer.innerHTML = '';
        barTagsContainer.style.display = 'none';
      } else {
        barTagsContainer.style.display = '';
        barTagsContainer.innerHTML = '';
        tags.forEach((tagEl) => {
          const link = tagEl.querySelector('a');
          if (!link) return;
          const text = link.querySelector('span:first-child')?.textContent?.trim() || '';
          const href = link.getAttribute('href') || '#';
          // Build via innerHTML so the <a> is already inside <facet-remove> when the
          // custom element constructor runs (it immediately queries for the inner <a>)
          const temp = document.createElement('span');
          temp.innerHTML = `<facet-remove><a href="${href}" class="collection-filter-bar__tag"><span class="collection-filter-bar__tag-text">${text}</span><svg class="collection-filter-bar__tag-remove" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></a></facet-remove>`;
          barTagsContainer.appendChild(temp.firstElementChild);
        });
      }
    }

    // Update promo tile visibility based on whether any filter is selected
    updatePromoTileVisibility();
    // Re-measure the bar height in case tag pills changed the bar's height
    syncFilterBarHeight();
  }

  // Re-run label sync after Shopify section re-renders (facets.js fires this event)
  document.addEventListener('shopify:section:load', () => {
    reinitSortLabel();
    syncFilterBar();
  });

  // Hook into facets.js renderPage completion (it swaps innerHTML of ProductGridContainer)
  // Observe for changes to re-sync the sort label and lifestyle mode
  const observer = new MutationObserver(() => {
    reinitSortLabel();
    syncTileImageSizes();
    watchGridAppends();

    // Re-apply lifestyle mode if active
    const penguinBtn = document.querySelector('[data-armada-penguin-toggle].is-active');
    if (penguinBtn) {
      const grid = document.getElementById('ProductGridContainer');
      if (grid) grid.classList.add('armada-lifestyle-mode');
    }
  });

  // Watch the panel's active-facets-mobile for re-render by facets.js
  const activeFacetsObserver = new MutationObserver(syncFilterBar);

  function startActiveFacetsObserver() {
    const el = document.querySelector('.active-facets-mobile');
    if (el) activeFacetsObserver.observe(el, { childList: true, subtree: true });
  }

  // AAS-5634: load more appends tiles straight into #product-grid, with the
  // small-tile `sizes`. Only those new items are synced. facets.js replaces the
  // list on every re-render, so the observer above re-attaches this one.
  const appendObserver = new MutationObserver((records) => {
    if (getTileSize() !== 'large') return;
    const tiles = [];
    records.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType === 1) tiles.push(...node.querySelectorAll('[data-img-sizes-large]'));
      });
    });
    if (tiles.length) syncTileImageSizes(tiles);
  });

  function watchGridAppends() {
    appendObserver.disconnect();
    const list = document.getElementById('product-grid');
    if (list) appendObserver.observe(list, { childList: true });
  }

  function startObserver() {
    const grid = document.getElementById('ProductGridContainer');
    if (grid) {
      observer.observe(grid, { childList: true, subtree: false });
      watchGridAppends();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      startObserver();
      startActiveFacetsObserver();
    });
  } else {
    init();
    startObserver();
    startActiveFacetsObserver();
  }
})();
